import { Component, OnInit, Inject, OnDestroy, Input, ViewChild, ElementRef } from '@angular/core';

import { Subscription } from 'rxjs';
import { MenuItem } from 'primeng/api';

import { PaginatorServiceInterface } from '../../services/paginator.service.interface';
import { PageCriteriaModelInterface } from '../../models/page-criteria.model.interface';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ActionKeys } from '../../../Forms/enums/keys.enum';

@Component({
    selector: 'agpr-paginator',
    templateUrl: './paginator.component.html',
    styleUrls: ['./paginator.component.scss'],
    providers: [],
    standalone: false
})
export class PaginatorComponent implements OnInit, OnDestroy {
    readonly agprComponentStyle = AgprComponentStyleEnum;

    @Input() id: string = null;
    @Input() maxNumberOfItems?: number;
    @Input() isDisabled: boolean = false;
    @Input() targetingMode: boolean = false;
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.default;
    @Input() isSyndicateRoute: boolean = false;

    @ViewChild('dropdownTrigger') trigger: ElementRef;
    @ViewChild('pageInput', { static: true }) pageInput: ElementRef;

    itemsPerPageOptions: MenuItem[] = [];

    itemsPerPageSelected: number = 25;
    pageNumber: number = 1;
    totalNumberOfPages: number = 1;
    totalMentionCount: number = null;
    dropdownOpen: boolean = false;
    backButtonDisabled: boolean = true;
    forwardButtonDisabled: boolean = false;

    private subscriptions: Subscription[] = [];
    private pageCriteria: PageCriteriaModelInterface;

    constructor(@Inject('PaginatorServiceInterface') public paginatorService: PaginatorServiceInterface) {}

    ngOnInit() {
        this.itemsPerPageOptions = [
            {
                id: '25',
                command: () =>  this.itemsPerPageChange(25)
            },
            {
                id: '50',
                command: () => this.itemsPerPageChange(50)
            },
            {
                id: '100',
                command: () => this.itemsPerPageChange(100)
            },
            {
                id: this.targetingMode ? '250' : '200',
                command: () => this.itemsPerPageChange(this.targetingMode ? 250 : 200)
            }
        ];

        if (this.isSyndicateRoute) {
            this.itemsPerPageOptions.pop();
        }

        this.subscriptions.push(
            this.paginatorService.getPageCriteria().subscribe(
                (searchCriteria) => {
                    if (searchCriteria) {
                        this.pageCriteria = searchCriteria;
                        this.pageNumber = (this.totalNumberOfPages === 0) ? 0 : searchCriteria.page;
                        this.itemsPerPageSelected = searchCriteria.pageSize;
                        this.enableDisableForwardAndBackButtons();
                    }
                }
            )
        );

        this.subscriptions.push(
            this.paginatorService.getItemCount().subscribe(
                (count) => {
                    if (count !== null) { // the following line calculates a whole number for total pages
                        const itemsPerPage = this.itemsPerPageSelected;
                        if (this.maxNumberOfItems && this.maxNumberOfItems >= 0) {
                            this.totalNumberOfPages = Math.ceil(Math.min(count, this.maxNumberOfItems) / itemsPerPage);
                        } else {
                            this.totalNumberOfPages = Math.ceil(count / itemsPerPage);
                        }
                        this.paginatorService.setMaxPageCriteria(this.totalNumberOfPages);
                        // clamp pagenumber
                        this.pageNumber = (this.totalNumberOfPages === 0) ? 0 :  Math.max(1, Math.min(this.totalNumberOfPages, this.pageNumber));
                        this.totalMentionCount = count;
                        this.enableDisableForwardAndBackButtons();
                    }
                }
            )
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach((each) => {
            each.unsubscribe();
        });
    }

    /**
     * Closes the drop down for the number of items per page
     */
    closeItemsPerPageDropdown() {
        this.dropdownOpen = false;
    }

    /**
     * Event handler for selecting number of items per page
     * @param numOfResults
     */
    itemsPerPageChange(numOfResults): void {
        this.paginatorService.canSetPageCriteria()
            .subscribe((can) => {
                if (can) {
                    this.toggleDropdown();
                    this.itemsPerPageSelected = numOfResults;
                    this.pageCriteria.pageSize = numOfResults;
                    this.pageNumber = (this.totalNumberOfPages === 0) ? 0 : 1;
                    this.pageCriteria.page = this.pageNumber;
                    this.paginatorService.setPageCriteria(this.pageCriteria);
                } else {
                    this.itemsPerPageSelected = this.pageCriteria.pageSize;
                }
            });
    }

    pageBackClick(): void {
        this.paginatorService.canSetPageCriteria()
            .subscribe((can) => {
                if (can) {
                    if (this.pageCriteria.page > 0) {
                        this.pageCriteria.page--;
                        this.paginatorService.setPageCriteria(this.pageCriteria);
                    }
                }
            });
    }

    pageForwardClick(): void {
        this.paginatorService.canSetPageCriteria()
            .subscribe((can) => {
                if (can) {
                    this.pageCriteria.page++;
                    this.paginatorService.setPageCriteria(this.pageCriteria);
                }
            });
    }

    /**
     * Enables/Disables forward and back buttons as appropriate
     */
    enableDisableForwardAndBackButtons(): void {
        if (this.pageNumber <= 1) {
            this.backButtonDisabled = true;
        } else {
            this.backButtonDisabled = false;
        }

        // when total mention count < max mention count, user can only navigate to the last page

        // if maxNumberOfItems is not defined.. we do ||
        if (!this.maxNumberOfItems && (this.pageNumber >= this.totalNumberOfPages || this.totalMentionCount < this.maxNumberOfItems)) {
            this.forwardButtonDisabled = true;

        // if maxNumberOfItems is defined we do &&
        } else if (!!this.maxNumberOfItems && (this.pageNumber >= this.totalNumberOfPages && this.totalMentionCount < this.maxNumberOfItems)) {
            this.forwardButtonDisabled = true;

        } else {
            this.forwardButtonDisabled = false;
        }

        // when total mention count > max mention count, user can navigate over last page
        if (this.pageNumber >= this.totalNumberOfPages && this.totalMentionCount > this.maxNumberOfItems) {
            this.backButtonDisabled = false;
            this.forwardButtonDisabled = false;
        }
    }

    /**
     * Handles keyup events on Input box.  If keyup event on enter key
     * calls onInput
     *
     * @param event input box keyup event
     */
    onKeyUp(event): void {
        if (event.key === 'Enter') {
            this.onInput(event);
        }
    }

    selectPageSize(item: any, event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        item.command();
    }

    /**
     * Validates input from Manual Page Number entry and updates Paginator Service
     * @param event input $event
     */
    onInput(event): void {
        const newPage = +event.target.value;
        if (newPage === this.pageNumber) {
            return;
        }
        this.paginatorService.canSetPageCriteria()
            .subscribe((can) => {
                if (can) {
                    if (1 <= newPage && newPage <= this.totalNumberOfPages && newPage !== this.pageNumber) { // input is valid
                        this.pageNumber = newPage;
                        this.pageCriteria.page = this.pageNumber;
                        this.paginatorService.setPageCriteria(this.pageCriteria);
                    } else {
                        this.pageNumber = (this.totalNumberOfPages === 0) ? 0 : this.pageCriteria.page;
                        this.pageInput.nativeElement.value = this.pageNumber;
                    }
                } else {
                    this.pageNumber = (this.totalNumberOfPages === 0) ? 0 : this.pageCriteria.page;
                }
            });
    }

    toggleDropdown(): void  {
        this.dropdownOpen = !this.dropdownOpen;
        if (this.dropdownOpen) {
            requestAnimationFrame(() => {
                (document.querySelector('li.agpr-paginatorItemsPerPageDropdown-selectItem--selected') as HTMLElement).focus();
            });
        } else {
            this.trigger.nativeElement.focus();
        }
    }

    onTriggerKeydown(event: KeyboardEvent): void {
        if (event.key === ActionKeys.DOWN && !this.dropdownOpen) {
            this.toggleDropdown();
        }
        if (event.key === ActionKeys.ESCAPE && this.dropdownOpen) {
            this.toggleDropdown();
        }
    }

    onOptionKeydown(index: number, event: KeyboardEvent): void {
        if (event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE) {
            this.itemsPerPageOptions[index].command({originalEvent: event});
        }
        if ((event.key === ActionKeys.UP && index === 0) || event.key === ActionKeys.TAB || event.key === ActionKeys.ESCAPE) {
            this.toggleDropdown();
        }
    }
}
